// src/app/features/payments/checkout/components/promo-section/PromoSection.tsx
import { Tag, Check, AlertCircle } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { runWithNetworkLifecycle } from "@/app/shared/lifecycle";
import { CheckoutPromoController } from "@/app/core/ProfileComp/checOut/controller/CheckoutPromoController";
import type { CheckoutPreviewResponse } from '@/app/data/profilPayment/api/PaymentsApi';
import type { CheckoutLocationState } from "@/app/core/TokensPayment/checkOutComp/subscribe/shared/checkoutTypes";
import type { SectionStateChangeHandler } from "../sections/types";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

interface PromoSectionProps {
  product: CheckoutLocationState;
  appliedPromo: string | null;
  onPreviewApplied: (preview: CheckoutPreviewResponse) => void;
  onStateChange?: SectionStateChangeHandler;
}

export function PromoSection({ product, appliedPromo, onPreviewApplied, onStateChange }: PromoSectionProps) {
  const promoController = useMemo(() => new CheckoutPromoController(), []);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setPromoCode(appliedPromo ?? "");
  }, [appliedPromo]);

  const handleApplyPromo = async () => {
    if (isApplying) return;

    const result = await runWithNetworkLifecycle({
      source: "CheckoutPromo",
      lifecycle: { onStateChange },
      meta: { promoCode, productCode: product.productCode },
      onStart: () => {
        setPromoError("");
        setIsApplying(true);
      },
      onFinally: () => setIsApplying(false),
      onError: (message) => setPromoError(message),
      task: () => promoController.applyPromoCode(promoCode, {
        productCode: product.productCode,
        currency: 'RUB',   // или XTR, в зависимости от выбора пользователя
      }),
      onSuccess: (res) => {
        if (res.error) return { ok: false, message: res.error };
        if (!res.appliedPromo || !res.preview) return { ok: false, message: t('promoNotApplied') };
        return { ok: true };
      },
    });

    if (!result) return;
    onPreviewApplied(result.preview!);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="mb-6 rounded-2xl border border-white/20 dark:border-white/20 bg-white/5 dark:bg-white/5 backdrop-blur-sm p-6 shadow-sm"
    >
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('promoCode')}</h2>
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder={t('enterPromoCode')}
            className="w-full rounded-xl border-2 border-border bg-transparent py-3 pl-10 pr-4 text-sm font-medium text-foreground placeholder:text-muted-foreground focus:border-violet-500 focus:outline-none"
          />
        </div>
        <button
          onClick={handleApplyPromo}
          disabled={!promoCode || isApplying}
          className="rounded-xl bg-violet-600 px-6 py-3 text-sm font-semibold text-white disabled:opacity-50 hover:bg-violet-700"
        >
          {isApplying ? "..." : t('apply')}
        </button>
      </div>
      {appliedPromo && (
        <div className="mt-3 flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
          <Check className="h-4 w-4" /> {t('promoApplied')} {appliedPromo}
        </div>
      )}
      {promoError && (
        <div className="mt-3 flex items-center gap-2 text-sm font-medium text-destructive">
          <AlertCircle className="h-4 w-4" /> {promoError}
        </div>
      )}
    </motion.div>
  );
}